import type { HttpErrorResponse } from '@angular/common/http';

/** PrimeNG `p-tag` severities, which is what these chips render as. */
export type CostSeverity = 'success' | 'secondary' | 'warn';

const MICROS_PER_UNIT = 1_000_000;

/**
 * The cost statuses the API documents today. The set is open; see {@link costStatusLabel}.
 *
 * A trace and a span do not use the same set. A trace's comes from `_cost_status_and_reason` in
 * `web/traces.py`; a span's comes from `ggx_otel.cost.CostStatus` and is only `priced`, `unpriced`
 * or `not_applicable`. `no_metered_spans` and `not_applicable` are the same statement at the two
 * levels — there was nothing here to price — so they read and colour the same way.
 */
export const COST_STATUS = {
    PRICED: 'priced',
    PARTIAL: 'partial',
    UNPRICED: 'unpriced',
    MIXED_CURRENCY: 'mixed_currency',
    NO_METERED_SPANS: 'no_metered_spans',
    NOT_APPLICABLE: 'not_applicable',
} as const;

const COST_STATUS_LABELS: Record<string, string> = {
    [COST_STATUS.PRICED]: 'Priced',
    [COST_STATUS.PARTIAL]: 'Partly priced',
    [COST_STATUS.UNPRICED]: 'Unpriced',
    [COST_STATUS.MIXED_CURRENCY]: 'Mixed currency',
    [COST_STATUS.NO_METERED_SPANS]: 'Nothing to price',
    [COST_STATUS.NOT_APPLICABLE]: 'Nothing to price',
};

/**
 * `duration_ms` as a string. Milliseconds below a second, seconds above it.
 * @param milliseconds The duration the API reported.
 * @returns The duration, or an em dash when there is none.
 */
export function formatDuration(milliseconds: number | null | undefined): string {
    if (milliseconds == null || Number.isNaN(milliseconds)) return '—';
    if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`;

    return `${(milliseconds / 1000).toFixed(2)} s`;
}

/**
 * Prompt and completion tokens as `1,088 → 41`.
 * @param input Prompt tokens, which the API omits for a non-metered span.
 * @param output Completion tokens, likewise.
 * @returns The pair, or an em dash when neither side has a value.
 */
export function formatTokens(
    input: number | null | undefined,
    output: number | null | undefined,
): string {
    const prompt = input ?? 0;
    const completion = output ?? 0;
    if (prompt === 0 && completion === 0) return '—';

    return `${prompt.toLocaleString('en-US')} → ${completion.toLocaleString('en-US')}`;
}

/**
 * A cost total against the currency it was computed in.
 *
 * Micros are meaningless without their currency, so a total with no currency renders as missing
 * rather than as a bare number. Totals are never added together across currencies anywhere in this
 * feature: the API refuses to do it (`mixed_currency`) and so does the UI.
 * @param micros The total, in millionths of one unit of `currency`.
 * @param currency The currency those micros were computed in.
 * @returns The total against its currency, or an em dash when either half is missing.
 */
export function formatCost(
    micros: number | null | undefined,
    currency: string | null | undefined,
): string {
    if (micros == null || !currency) return '—';

    const amount = micros / MICROS_PER_UNIT;
    // Up to six decimals, because a single cheap span costs USD 0.000188, with trailing zeros cut.
    const rendered = amount.toFixed(6).replace(/\.?0+$/, '');

    return `${currency} ${rendered === '' ? '0' : rendered}`;
}

/**
 * A readable name for a cost status.
 *
 * `_cost_status_and_reason` in `web/traces.py` passes an unrecognised code straight through rather
 * than mapping it onto a closed set, so a code added on the ingest side reaches this function. An
 * unknown code is made readable instead of being dropped or mislabelled.
 * @param status The code the API sent.
 * @returns A human-readable name for it.
 */
export function costStatusLabel(status: string | null | undefined): string {
    if (!status) return 'Unknown';
    const known = COST_STATUS_LABELS[status];
    if (known) return known;

    const words = status.replace(/_/g, ' ').trim();

    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * How a cost status is coloured. Anything not known to be complete is a warning, never a success.
 * @param status The code the API sent.
 * @returns The `p-tag` severity to render it with.
 */
export function costStatusSeverity(status: string | null | undefined): CostSeverity {
    if (status === COST_STATUS.PRICED) return 'success';
    if (status === COST_STATUS.NO_METERED_SPANS || status === COST_STATUS.NOT_APPLICABLE) {
        return 'secondary';
    }

    return 'warn';
}

/**
 * The message to show for a failed trace request.
 *
 * The trace endpoints fill `error.message`; some of them carry the specific text in `debugMsg`
 * instead. This reads whichever is there, and is deliberately a tracing-only helper rather than a
 * change to `Helpers.getErrorMessageString`: widening that one would start surfacing backend debug
 * text to users on every older endpoint in the product, which is a platform decision and not one
 * for this screen to make.
 * @param errorResponse The failed response, whatever shape it arrived in.
 * @returns The message, or an empty string when the body carries none.
 */
export function traceErrorMessage(errorResponse: HttpErrorResponse | null | undefined): string {
    const error = errorResponse?.error?.error;

    return error?.message || error?.debugMsg || '';
}

/** The span kinds the drain prices. Only these can be "unpriced"; the rest have nothing to price. */
const METERED_KINDS: ReadonlySet<string> = new Set(['llm', 'embedding']);

/**
 * Whether a span of this kind is one the price sheet applies to.
 *
 * A cost tag belongs on an LLM or embedding span. On a chain, tool or retriever span "Nothing to
 * price" is not information, it is noise on every non-model step of every trace.
 * @param kind The span kind the API sent.
 * @returns True for the kinds that carry a cost.
 */
export function isMeteredKind(kind: string | null | undefined): boolean {
    return kind != null && METERED_KINDS.has(kind);
}

/** The platform's own session ids, as `corridor_api.tracing.platform.keys.chat_session_id` writes them. */
const PLATFORM_CHAT_SESSION = /^chat:test-run:(\d+)$/;
/** A simulation job's session, as `corridor_api.tracing.platform.keys.simulation_session_id` writes it. */
const SIMULATION_SESSION = /^simulation:(\d+)$/;

/**
 * The session a simulation job's traces are grouped under: one trace per record it ran.
 * @param simulationId The simulation job.
 * @returns Its session id, as the trace headers carry it.
 */
export function simulationSessionId(simulationId: number): string {
    return `simulation:${simulationId}`;
}

/**
 * Whether a session is a simulation job's: its traces are records of a batch, not turns of a chat.
 * @param sessionId The session, as the trace headers carry it.
 * @returns True for a session `simulationSessionId` names.
 */
export function isSimulationSession(sessionId: string | null | undefined): boolean {
    return !!sessionId && SIMULATION_SESSION.test(sessionId);
}

/** The tag on one simulation record's trace, as `corridor_api.tracing.platform.keys.record_tag` writes it. */
const RECORD_TAG = /^record:(\d+)$/;
/** The tag shared by one multi-turn conversation's traces, as `conversation_tag` writes it. */
const CONVERSATION_TAG = /^conversation:(.+)$/;

/**
 * The tag that finds one simulation record's trace: records are numbered from 1 as the input data is.
 * @param recordNumber The record's number.
 * @returns The tag its trace carries.
 */
export function recordTag(recordNumber: number): string {
    return `record:${recordNumber}`;
}

/**
 * Which simulation record a trace is, from its tags.
 * @param tags The trace's tags.
 * @returns The record's number, or null when the trace is not a record's.
 */
export function recordNumber(tags: string[] | null | undefined): number | null {
    for (const tag of tags ?? []) {
        const match = RECORD_TAG.exec(tag);
        if (match) return Number(match[1]);
    }

    return null;
}

/**
 * Which multi-turn conversation a trace belongs to, from its tags.
 * @param tags The trace's tags.
 * @returns The conversation's id, or null when the trace is not part of one.
 */
export function conversationId(tags: string[] | null | undefined): string | null {
    for (const tag of tags ?? []) {
        const match = CONVERSATION_TAG.exec(tag);
        if (match) return match[1];
    }

    return null;
}

/**
 * A session id as a reader should see it.
 *
 * The platform namespaces the sessions it creates (`chat:test-run:265`) so they cannot collide
 * with a customer's own ids. That key is right for the API and wrong for a screen: shown, it
 * reads as an internal identifier leaking. A customer's own id is shown as they sent it.
 * @param sessionId The id the API sent.
 * @returns A label for it, or an em dash when there is none.
 */
export function formatSessionLabel(sessionId: string | null | undefined): string {
    if (!sessionId) return '—';
    const chat = PLATFORM_CHAT_SESSION.exec(sessionId);

    if (chat) return `Chat #${chat[1]}`;
    const simulation = SIMULATION_SESSION.exec(sessionId);

    return simulation ? `Simulation #${simulation[1]}` : sessionId;
}
